/**
 * Transaction Logger Service
 * Handles all transaction logging operations for the application
 * Provides a centralized, reusable interface for logging various transaction types
 */

import Api from '../../../../lib/api';
import {
  LOG_TYPE_PI_CREATION,
  LOG_TYPE_PI_UPDATED,
  LOG_TYPE_DOC_UPLOADED,
  LOG_TYPE_DOC_DELETED,
  LOG_TYPE_CERT_ISSUED,
  LOG_TYPE_STATUS_CHANGED,
  LOG_TYPE_ARCHIVED,
  LOG_TYPE_UNARCHIVED,
  LOG_TYPE_CONFIRMED,
  LOG_TYPE_UNCONFIRMED,
  LOG_TYPE_WAIVED,
  LOG_TYPE_UNDO_WAIVED,
  ENTITY_TYPE_DOCUMENT,
  ENTITY_TYPE_INSURED,
  ENTITY_TYPE_PROJECT_INSURED,
  ENTITY_TYPE_PROJECT,
  ENTITY_TYPE_CERTIFICATE,
} from '../../../../lib/appConstants';
const crypto = require('crypto');

/**
 * Transaction Logger Class
 * Encapsulates transaction logging logic
 * Uses the existing Api.postTransactions() method
 */
class TransactionLogger {
  /**
   * Get authentication token
   * @returns {string|null} Auth token
   */
  static getAuthToken() {
    return localStorage.getItem('auth-token') || sessionStorage.getItem('auth-token');
  }

  /**
   * Generate idempotency key for transaction logging
   * @returns {string} Unique idempotency key
   */
  static generateIdempotencyKey() {
     return `txn_${crypto.randomBytes(16).toString('hex')}`;
  }

  /**
   * Build transaction payload based on your API structure
   * @param {Object} data - Transaction data
   * @returns {Object} Formatted transaction object
   */
  static buildTransactionPayload(data) {
    const {
      performedByUserId,
      performedByDisplay,
      holderId,
      projectId,
      insuredId,
      projectInsuredId,
      entityId,
      transactionTypeCode,
      entityTypeCode,
      idempotencyKey,
      metadata = {},
    } = data;

    return {
      performedByUserId,
      performedByDisplay,
      holderId,
      projectId,
      insuredId,
      projectInsuredId,
      entityId,
      transactionTypeCode,
      entityTypeCode,
      idempotencyKey,
      metadata,
      ...(data.documentId && { documentId: data.documentId }),
      ...(data.certificateId && { certificateId: data.certificateId }),
      ...(data.status && { complianceStatus: data.status }),
    };
  }

  /**
   * Log a transaction to the transaction service
   * @param {Object} transactionData - Transaction data object
   * @returns {Promise} Api response
   */
  static async logTransaction(transactionData) {
    try {
      const payload = this.buildTransactionPayload(transactionData);
      const token = this.getAuthToken();
      
      const response = await Api.postTransactions('transactions', payload, token);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Transaction logging error:', error);
      return {
        success: false,
        error: (error.response && error.response.data) || error.message,
      };
    }
  }

  /**
   * Log Project Insured Creation
   * @param {Object} data - PI creation data
   * @returns {Promise} Transaction result
   */
  static async logProjectInsuredCreation(data) {
    return this.logTransaction({
      ...data,
      transactionTypeCode: LOG_TYPE_PI_CREATION,
      entityTypeCode: ENTITY_TYPE_PROJECT_INSURED,
    });
  }

  /**
   * Log Project Insured Update
   * @param {Object} data - PI update data
   * @returns {Promise} Transaction result
   */
  static async logProjectInsuredUpdate(data) {
    return this.logTransaction({
      ...data,
      transactionTypeCode: LOG_TYPE_PI_UPDATED,
      entityTypeCode: ENTITY_TYPE_PROJECT_INSURED,
    });
  }

    /**
   * Log Archive Action
   * @param {Object} data - Archive data
   * @returns {Promise} Transaction result
   */
  static async logArchive(data) {
    return this.logTransaction({
      ...data,
      transactionTypeCode: LOG_TYPE_ARCHIVED,
    });
  }


  /**
   * Log Unarchive Action
   * @param {Object} data - Unarchive data
   * @returns {Promise} Transaction result
   */
  static async logUnarchive(data) {
    return this.logTransaction({
      ...data,
      transactionTypeCode: LOG_TYPE_UNARCHIVED,
    });
  }

}

export default TransactionLogger;

